import React, { useEffect, useState } from "react";
import { Box, IconButton, Tooltip } from "@mui/material";
import { useAppStore } from "../../../stores/appStore";
import { ResponseContextMenu } from "./response-context-menu";
import {
  getPersonaDescription,
  getPersonaDisplayName,
  getPersonaLabel,
} from "../../../utils/responseContextUtils";
import {
  avatarFallbackStyles,
  buttonContentStyles,
  ccLettersStyles,
  getButtonSx,
  leftSlotStyles,
  personaNameStyles,
} from "./button-styles";

interface ResponseContextButtonProps {
  disabled?: boolean;
}

const DefaultAvatarIcon = () => (
  <svg
    width={12}
    height={12}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

export const ResponseContextButton: React.FC<ResponseContextButtonProps> = ({
  disabled = false,
}) => {
  const { responseContext, responseContextMenu, fetchResponseContextMenu } =
    useAppStore();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    void fetchResponseContextMenu().catch((error) => {
      console.error("Failed to preload response context menu:", error);
    });
  }, [fetchResponseContextMenu]);

  const personaLabel = getPersonaLabel(responseContext);
  const displayName = getPersonaDisplayName(
    responseContext,
    responseContextMenu,
  );
  const description = getPersonaDescription(
    responseContext,
    responseContextMenu,
  );
  const isActive = Boolean(personaLabel) || !responseContext.is_default;

  const tooltipTitle = description || "";

  return (
    <>
      <Tooltip
        title={tooltipTitle}
        placement="top"
        disableHoverListener={!tooltipTitle}
      >
        <IconButton
          disabled={disabled}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          aria-label={description ?? undefined}
          sx={getButtonSx(isActive)}
        >
          <Box sx={buttonContentStyles}>
            <Box sx={leftSlotStyles}>
              {personaLabel ? (
                <Box component="span" sx={personaNameStyles}>
                  {personaLabel}
                </Box>
              ) : (
                <Box sx={avatarFallbackStyles}>
                  <DefaultAvatarIcon />
                </Box>
              )}
            </Box>
            {displayName ? (
              <Box component="span" sx={ccLettersStyles}>
                {displayName}
              </Box>
            ) : null}
          </Box>
        </IconButton>
      </Tooltip>
      <ResponseContextMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      />
    </>
  );
};
